import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IMPORTANCE_KEYS, importanceMeta } from "@/lib/taskUi";
import { CategorySelect } from "@/components/tasks/CategorySelect";
import { Paperclip, X } from "lucide-react";
import { DEFAULT_ASSIGNMENT_LABELS, DEFAULT_ASSIGNMENT_KEYS } from "@/lib/defaultAssignment";
import { AssigneeSelect } from "@/components/tasks/AssigneeSelect";
import { toast } from "sonner";

const NONE = "__none__";

// lockPerson=true hides the person picker (person supplied via defaults.person_id) and instead
// offers the Person's Recruiting Opportunities for optional linking.
export function TaskFormDialog({ open, onClose, onCreated, defaults = {}, lockPerson = false, title = "Add Task", milestones = null, templateMode = false }) {
  const blank = () => ({ title: "", assignee_id: "", assignee_person_id: "", importance: "normal", due_date: "", due_time: "",
    category: "", status: "", person_id: "", opportunity_id: "", project_id: "", description: "",
    candidate_visible: false, recurrence_frequency: "", ...defaults });
  const [form, setForm] = useState(blank());
  const [users, setUsers] = useState([]);
  const [cats, setCats] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [opps, setOpps] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [more, setMore] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(blank()); setMore(false); setFiles([]);
    Promise.all([
      api.get("/tasks/assignable-users").catch(() => ({ data: { items: [] } })),
      api.get("/task-categories").catch(() => ({ data: { items: [] } })),
      api.get("/task-statuses").catch(() => ({ data: { items: [] } })),
      lockPerson ? Promise.resolve({ data: { persons: [] } }) : api.get("/persons", { params: { page_size: 300 } }).catch(() => ({ data: { persons: [] } })),
      api.get("/projects").catch(() => ({ data: { items: [] } })),
    ]).then(([u, c, s, p, pr]) => {
      setUsers(u.data.items || []); setCats(c.data.items || []); setStatuses(s.data.items || []);
      setPeople(p.data.persons || []); setProjects(pr.data.items || []);
    });
    const pid = defaults.person_id;
    if (pid) {
      api.get("/opportunities", { params: { person_id: pid, page_size: 100 } })
        .then((r) => setOpps(r.data.opportunities || [])).catch(() => setOpps([]));
    } else setOpps([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addFiles = (e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (again) => {
    if (!form.title.trim()) return toast.error("Task title is required");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(), importance: form.importance,
        assignee_id: form.assignee_id || null, assignee_person_id: form.assignee_person_id || null,
        due_date: form.due_time && form.due_date ? `${form.due_date}T${form.due_time}` : (form.due_date || null),
        due_time: form.due_time || null, category: form.category || null, status: form.status || null,
        person_id: form.person_id || null, opportunity_id: form.opportunity_id || null,
        project_id: form.project_id || null, milestone_id: (milestones && form.milestone_id) || null, description: form.description || null,
        default_assignment: templateMode ? (form.default_assignment || "unassigned") : null,
        support_conversation_id: form.support_conversation_id || null,
        candidate_visible: form.candidate_visible, show_on_team_calendar: !!form.show_on_team_calendar,
        recurrence: form.recurrence_frequency ? { frequency: form.recurrence_frequency, interval: 1 } : null,
      };
      const { data } = await api.post("/tasks", payload);
      // Upload any attachments against the created task (internal visibility by design).
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try { await api.post(`/tasks/${data.id}/attachments`, fd); }
        catch (e) { toast.error(`Attachment "${f.name}" failed: ${apiError(e)}`); }
      }
      toast.success("Task created");
      onCreated?.(data);
      if (again) { setForm({ ...blank(), assignee_id: form.assignee_id, importance: form.importance }); setFiles([]); }
      else onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="task-form-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle>
          <DialogDescription>{lockPerson ? "Create a follow-up task linked to this person." : "Quickly capture what needs to get done."}</DialogDescription></DialogHeader>
        <div className="space-y-3 max-h-[64vh] overflow-y-auto tr-scroll pr-1">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Task Title *</Label>
            <Input autoFocus value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="task-title-input" placeholder="e.g. Call candidate about interview" />
            <label className="flex items-center gap-2 mt-2 text-xs text-slate-700" data-testid="task-team-calendar-box">
              <Checkbox checked={!!form.show_on_team_calendar} onCheckedChange={(v) => set("show_on_team_calendar", !!v)} data-testid="task-show-on-team-calendar" />
              Show on Team Calendar <span className="text-slate-400">(your team sees the title, date and time only)</span>
            </label>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Details</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="task-description"
              placeholder="Instructions or context for this task…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Due Date</Label>
              <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="task-due-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Priority</Label>
              <Select value={form.importance} onValueChange={(v) => set("importance", v)}>
                <SelectTrigger data-testid="task-importance-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {IMPORTANCE_KEYS.map((k) => <SelectItem key={k} value={k}>{importanceMeta(k).label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              {templateMode ? (<>
                <Label className="text-xs font-semibold text-slate-700">Default Assignment</Label>
                <Select value={form.default_assignment || "unassigned"} onValueChange={(v) => set("default_assignment", v)}>
                  <SelectTrigger data-testid="task-default-assignment-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{DEFAULT_ASSIGNMENT_KEYS.map((k) => <SelectItem key={k} value={k}>{DEFAULT_ASSIGNMENT_LABELS[k]}</SelectItem>)}</SelectContent>
                </Select></>) : (<>
              <Label className="text-xs font-semibold text-slate-700">Assigned To</Label>
              {form.person_id ? (
                <AssigneeSelect personId={form.person_id} assigneeId={form.assignee_id} assigneePersonId={form.assignee_person_id} noneLabel="Me"
                  onChange={(v) => setForm((f) => ({ ...f, ...v, assignee_id: v.assignee_id || "" }))} />
              ) : (
              <Select value={form.assignee_id || NONE} onValueChange={(v) => set("assignee_id", v === NONE ? "" : v)}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Me" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  <SelectItem value={NONE}>Me</SelectItem>
                  {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>)}</>)}
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700">Category</Label>
              <CategorySelect value={form.category} onChange={(v) => set("category", v || "")} cats={cats} setCats={setCats} />
            </div>
          </div>
          {milestones && (
            <div>
              <Label className="text-xs font-semibold text-slate-700">Milestone</Label>
              {milestones.length ? (
                <Select value={form.milestone_id || NONE} onValueChange={(v) => set("milestone_id", v === NONE ? "" : v)}>
                  <SelectTrigger data-testid="task-milestone-select"><SelectValue placeholder="No Milestone" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    <SelectItem value={NONE}>No Milestone</SelectItem>
                    {milestones.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : <p className="text-xs text-slate-400 mt-1" data-testid="task-milestone-none">No milestones available</p>}
            </div>
          )}
          {(opps.length > 0 && lockPerson) && (
            <div>
              <Label className="text-xs font-semibold text-slate-700">Related Opportunity</Label>
              <Select value={form.opportunity_id || NONE} onValueChange={(v) => set("opportunity_id", v === NONE ? "" : v)}>
                <SelectTrigger data-testid="task-opportunity-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  <SelectItem value={NONE}>Person only (no opportunity)</SelectItem>
                  {opps.map((o) => <SelectItem key={o.id} value={o.id}>{o.human_id} · {o.candidate_type || "No type"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold text-slate-700">Attachment</Label>
            <label className="mt-1 flex items-center gap-2 text-sm text-brand font-semibold cursor-pointer hover:underline w-fit" data-testid="task-attach-label">
              <Paperclip className="w-4 h-4" /> Add File
              <input type="file" className="hidden" multiple onChange={addFiles} data-testid="task-attach-input" />
            </label>
            {files.map((f, i) => (
              <div key={i} className="mt-1 flex items-center justify-between rounded border border-slate-200 px-2 py-1 text-xs text-slate-600" data-testid={`task-attach-file-${i}`}>
                <span className="truncate">{f.name}</span>
                <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          {!more && <button className="text-xs font-semibold text-brand hover:underline" onClick={() => setMore(true)} data-testid="task-more-options">+ More options</button>}
          {more && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Status</Label>
                  <Select value={form.status || NONE} onValueChange={(v) => set("status", v === NONE ? "" : v)}>
                    <SelectTrigger data-testid="task-status-select"><SelectValue placeholder="Not Started" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      <SelectItem value={NONE}>Default</SelectItem>
                      {statuses.filter((s) => s.active !== false).map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Due Time</Label>
                  <Input type="time" value={form.due_time} onChange={(e) => set("due_time", e.target.value)} data-testid="task-due-time" />
                </div>
              </div>
              {!lockPerson && (
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Related Person</Label>
                  <Select value={form.person_id || NONE} onValueChange={(v) => set("person_id", v === NONE ? "" : v)}>
                    <SelectTrigger data-testid="task-person-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      <SelectItem value={NONE}>None</SelectItem>
                      {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.human_id})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold text-slate-700">Project</Label>
                <Select value={form.project_id || NONE} onValueChange={(v) => set("project_id", v === NONE ? "" : v)}>
                  <SelectTrigger data-testid="task-project-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    <SelectItem value={NONE}>None</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Recurrence</Label>
                <Select value={form.recurrence_frequency || NONE} onValueChange={(v) => set("recurrence_frequency", v === NONE ? "" : v)}>
                  <SelectTrigger data-testid="task-recurrence-select"><SelectValue placeholder="Does not repeat" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value={NONE}>Does not repeat</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.candidate_visible} onCheckedChange={(v) => set("candidate_visible", !!v)} data-testid="task-candidate-visible" />
                Candidate visible
              </label>
            </>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => submit(true)} disabled={saving} data-testid="task-save-another">Save &amp; Add Another</Button>
          <Button onClick={() => submit(false)} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="task-save">Create Task</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
